const value = (source, key) => {
  if (source == null) return undefined;
  return typeof source.get === 'function' ? source.get(key) : source[key];
};

const nestedValue = (source, keys) =>
  keys.reduce((current, key) => value(current, key), source);

const centsFromAmount = (amount) => {
  const cents = value(amount, 'cents');
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
};

export const baselineCreditLimitCents = (attempt) =>
  centsFromAmount(nestedValue(attempt, ['baseline', 'approved_credit_limit']));

export const effectiveCreditLimitCents = (attempt) =>
  centsFromAmount(nestedValue(attempt, ['effective', 'approved_credit_limit'])) ??
  centsFromAmount(nestedValue(attempt, ['effective', 'effective_tcl', 'amount']));

const subLimitCents = (company) => {
  const amount = nestedValue(company, ['vc_sub_limits', 'limit_amount']);
  if (typeof amount !== 'string' || !/^\d+(?:\.\d{1,2})?$/.test(amount)) {
    return null;
  }

  const [dollars, fractional = ''] = amount.split('.');
  const cents = Number(`${dollars}${fractional.padEnd(2, '0')}`);
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
};

const centsFromDollars = (dollars) => {
  if (!Number.isFinite(dollars) || dollars <= 0) return null;

  const cents = Math.round(dollars * 100);
  return Number.isSafeInteger(cents) && cents / 100 === dollars ? cents : null;
};

export const isUpliftedResult = (attempt) => value(attempt, 'status') === 'uplifted';

export const dollarsFromCents = (amountMap) => {
  const cents = centsFromAmount(amountMap);
  return cents === null ? null : cents / 100;
};

export const creditLimitChange = (attempt) => {
  const baselineCents = baselineCreditLimitCents(attempt);
  const effectiveCents = effectiveCreditLimitCents(attempt);
  const improved =
    baselineCents !== null &&
    effectiveCents !== null &&
    effectiveCents > baselineCents;

  return {
    baseline: baselineCents === null ? null : baselineCents / 100,
    effective: effectiveCents === null ? null : effectiveCents / 100,
    delta: improved ? (effectiveCents - baselineCents) / 100 : 0,
    improved,
  };
};

export const creditLimitEventProperties = (attempt) => {
  const baselineCents = baselineCreditLimitCents(attempt);
  const effectiveCents = effectiveCreditLimitCents(attempt);

  return {
    baselineCreditLimitCents: baselineCents,
    effectiveCreditLimitCents: effectiveCents,
    creditLimitDeltaCents:
      baselineCents !== null && effectiveCents !== null
        ? Math.max(effectiveCents - baselineCents, 0)
        : 0,
  };
};

export const virtualCardEntitlement = ({ company, priorCompany }) => {
  const eligible = value(company, 'supports_virtual_card') === true;
  const active = value(nestedValue(company, ['vc_sub_limits']), 'enabled') === true;
  const currentSubLimitCents = active ? subLimitCents(company) : null;
  const priorSubLimitCents = subLimitCents(priorCompany);

  return {
    eligible,
    active,
    subLimit:
      currentSubLimitCents !== null && currentSubLimitCents > 0
        ? currentSubLimitCents / 100
        : null,
    newlyEligible: value(priorCompany, 'supports_virtual_card') === false && eligible,
    subLimitIncreased:
      value(nestedValue(priorCompany, ['vc_sub_limits']), 'enabled') === true &&
      currentSubLimitCents !== null &&
      priorSubLimitCents !== null &&
      currentSubLimitCents > priorSubLimitCents,
  };
};

export const linkedAccountSummary = ({ attempt, linkedAccount }) => {
  if (value(attempt, 'account_ready') !== true) return null;

  const mask = value(attempt, 'account_mask');
  if (typeof mask !== 'string' || !/^\d{1,4}$/.test(mask)) return null;

  const institution = value(linkedAccount, 'institution');
  const name = value(linkedAccount, 'name');
  const result = { mask };

  if (typeof institution === 'string' && institution.length <= 64) {
    result.institution = institution;
  }
  if (typeof name === 'string' && name.length <= 64) result.name = name;

  return result;
};

export const continuationTarget = ({ attempt }) => {
  if (!isUpliftedResult(attempt)) return null;
  const path = value(attempt, 'next_route_path');
  // The server also sets can_continue=false when no route is available yet.
  // Read the authoritative session route in that case rather than guessing one.
  if (!path) return { kind: 'route' };
  return value(attempt, 'can_continue') === true ? { kind: 'path', path } : null;
};

export const resultViewEventProperties = ({
  session,
  attempt,
  company,
  priorCompany,
}) => {
  if (!isUpliftedResult(attempt)) return null;

  const creditLimitProperties = creditLimitEventProperties(attempt);
  const entitlement = virtualCardEntitlement({ company, priorCompany });
  const virtualCardSubLimitCents = centsFromDollars(entitlement.subLimit);
  const hasShoppingCart =
    session && typeof session.hasShoppingCart === 'function'
      ? session.hasShoppingCart()
      : Boolean(value(session, 'shopping_cart_id'));
  const baselineTier = nestedValue(attempt, ['baseline', 'tier']);
  const effectiveTier = nestedValue(attempt, ['effective', 'tier']);

  return {
    sessionType: value(session, 'session_type'),
    hasShoppingCart,
    attemptStatus: 'uplifted',
    accountReady: value(attempt, 'account_ready') === true,
    underwritingEnhanced: value(attempt, 'company_underwriting_enhanced') === true,
    ...creditLimitProperties,
    tierChanged:
      typeof baselineTier === 'string' &&
      typeof effectiveTier === 'string' &&
      baselineTier !== effectiveTier,
    virtualCardEligible: entitlement.eligible,
    virtualCardActive: entitlement.active,
    virtualCardNewlyEligible: entitlement.newlyEligible,
    ...(entitlement.active
      ? { vcSubLimitCents: virtualCardSubLimitCents }
      : {}),
  };
};
